import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaTooth,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import {
  User,
  Phone,
  Heart,
  ClipboardList,
  Calendar,
  MapPin,
  Droplet,
  FileText,
} from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import { AuthContext } from "../../../context/AuthContext";
import user from "../../../assets/image/user.jpg";
import logo from "../../../assets/image/kkgt-header-image.jpg";
import {  } from "../../../data";

// Simplified animations without external dependencies
const AnimatedContainer = ({ children, className }) => (
  <div className={`transition-all duration-300 ease-in-out ${className}`}>
    {children}
  </div>
);

const UsersList = () => {

    const { dentalReasons, reasonOptions, timeBlocks } = useContext(AuthContext);


  const navigate = useNavigate();

  // State for API data
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Modal states
  const [checkupInfo, setCheckupInfo] = useState("");
  const [age, setAge] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Appointment States
  const [appointmentDate, setAppointmentDate] = useState("");
  const [timeBlock, setTimeBlock] = useState("");
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");

  // UI States
  const [showFilters, setShowFilters] = useState(false);
  const [expandedTiles, setExpandedTiles] = useState({});

  // Filter options
  const itemsPerPageOptions = [5, 10, 15, 20, 25, 50];

  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 14);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [
    debouncedSearchTerm,
    dateFrom,
    dateTo,
    sortBy,
    sortOrder,
    itemsPerPage,
    genderFilter,
    statusFilter,
  ]);

  // Fetch patients data from API
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder,
        role: "patient", // Only fetch patients
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(genderFilter && { gender: genderFilter }),
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await API.get(`/api/doctor/get-all-users?${params}`);

      if (response.data.success) {
        setAllPatients(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError("Failed to fetch patients");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching patients");
      console.error("Error fetching patients:", err);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
    dateFrom,
    dateTo,
    genderFilter,
    statusFilter,
  ]);

  // Load data when dependencies change
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Helper function to render pastDentalHistory items safely
  const renderDentalHistory = useCallback((history) => {
    if (!history) return "N/A";

    if (typeof history === "string") return history;

    if (Array.isArray(history)) {
      if (history.length === 0) return "No history";

      return history
        .map((item, index) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            return `${item.reason || "No reason"} ${item.id ? `(ID: ${item.id})` : ""
              }`;
          }
          return `Item ${index + 1}`;
        })
        .join(", ");
    }

    if (typeof history === "object") {
      return `${history.reason || "No reason"} ${history.id ? `(ID: ${history.id})` : ""
        }`;
    }

    return String(history);
  }, []);

  // Helper function to calculate age from date of birth
  const calculateAge = useCallback((dateOfBirth) => {
    const dob = new Date(dateOfBirth);
    const todayDate = new Date();
    let ageCalc = todayDate.getFullYear() - dob.getFullYear();
    const monthDifference = todayDate.getMonth() - dob.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && todayDate.getDate() < dob.getDate())
    ) {
      ageCalc--;
    }

    return ageCalc;
  }, []);

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Toggle the expansion of a user tile
  const toggleTile = useCallback((id) => {
    setExpandedTiles((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Open Modal for Chief Complaint
  const openModal = useCallback(
    (user) => {
      setSelectedUser(user);
      if (user.userDetails?.dateOfBirth) {
        const userAge = calculateAge(user.userDetails.dateOfBirth);
        setAge(userAge);
      } else {
        setAge("N/A");
      }
      setIsModalOpen(true);
    },
    [calculateAge]
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setCheckupInfo("");
    setAge(0);
  };

  // Open Modal for Appointment
  const openAppointmentModal = useCallback(
    (user) => {
      setSelectedUser(user);
      if (user.userDetails?.dateOfBirth) {
        const userAge = calculateAge(user.userDetails.dateOfBirth);
        setAge(userAge);
      } else {
        setAge("N/A");
      }
      setIsAppointmentModalOpen(true);
    },
    [calculateAge]
  );

  const closeAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
    setAppointmentDate("");
    setTimeBlock("");
    setReason("");
    setOtherReason("");
  };

  // Handle Appointment Submission
  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();

    if (!appointmentDate || !timeBlock || !reason) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (reason === "Other" && !otherReason.trim()) {
      toast.error("Please specify the reason for the visit.");
      return;
    }

    toast.loading("Creating Appointment...", {
      position: "top-center",
      toastId: "create-appointment",
    });

    try {
      await API.post(`/api/users/appointment`, {
        userId: selectedUser._id,
        date: appointmentDate,
        status: "Scheduled",

        timeBlock,
        reason: reason === "Others" ? otherReason : reason,
      });

      toast.dismiss("create-appointment");
      toast.success("Appointment created successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      closeAppointmentModal();
    } catch (error) {
      console.error(error);

      toast.dismiss("create-appointment");
      toast.error(
        error.response?.data?.message || "Failed to create appointment.",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
    }
  };

  // Handle Chief Complaint Submission
  const handleSubmit = async () => {
    if (!checkupInfo) {
      toast.error("Chief complaint is required.");
      return;
    }

    toast.loading("Creating OPD...", {
      position: "top-center",
      toastId: "create-opd",
    });

    try {
      const response = await API.post(`/api/doctor/existing-user-opd`, {
        age: typeof age === "number" ? age : null,
        checkupInfo,
        user: selectedUser._id,
        medicalDetails: selectedUser.medicalDetails._id,
        userDetails: selectedUser.userDetails._id,
      });

      toast.dismiss("create-opd");
      toast.success("OPD Created Successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      navigate(`/dashboard/checkup/${response.data}`);
      closeModal();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message || "Failed to create OPD.";

      toast.dismiss("create-opd");
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });

      setError(errorMessage);
      closeModal();
    }
  };

  // Handle page change
  const handlePageChange = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
      }
    },
    [totalPages]
  );

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  }, []);

  // Handle sort change
  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortBy(field);
        setSortOrder("desc");
      }
    },
    [sortBy, sortOrder]
  );

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setGenderFilter("");
    setStatusFilter("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Memoized Header Section
  const HeaderSection = useMemo(
    () => (
      <div className="text-center mt-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">All Patients</h1>
        <p className="text-gray-600">
          Manage patient records and create appointments
        </p>
      </div>
    ),
    []
  );

  if (loading && allPatients.length === 0) {
    return <Loading />;
  }

  return (
    <AnimatedContainer className="mx-auto p-6">
      {HeaderSection}

      {/* Quick Stats Summary */}
      {!loading && (
        <div className="mb-6 bg-blue-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {totalRecords}
              </div>
              <div className="text-sm text-gray-600">Total Patients</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {totalPages}
              </div>
              <div className="text-sm text-gray-600">Total Pages</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {currentPage}
              </div>
              <div className="text-sm text-gray-600">Current Page</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">
                {allPatients.length}
              </div>
              <div className="text-sm text-gray-600">Showing Now</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Controls Section */}
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-6 flex justify-center">
          <div className="relative w-full">
            <FaSearch className="absolute top-3 left-3 text-blue-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or mobile"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Search Patients"
            />
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <div className="flex justify-center mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
            {showFilters ? (
              <FaChevronUp className="ml-2" />
            ) : (
              <FaChevronDown className="ml-2" />
            )}
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Sort and Control Options */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSort("createdAt")}
              className={`px-4 py-2 rounded-lg border transition-colors ${sortBy === "createdAt"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
            >
              Sort by Date{" "}
              {sortBy === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
            </button>
            <button
              onClick={() => handleSort("userDetails.name")}
              className={`px-4 py-2 rounded-lg border transition-colors ${sortBy === "userDetails.name"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
            >
              Sort by Name{" "}
              {sortBy === "userDetails.name" &&
                (sortOrder === "asc" ? "↑" : "↓")}
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label htmlFor="itemsPerPage" className="font-semibold">
              Items Per Page:
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading patients...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 font-medium">Error: {error}</div>
            <button
              onClick={fetchPatients}
              className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Users List For Mobile */}
      <div className="block md:hidden space-y-6">
        {!loading && allPatients?.length > 0 ? (
          allPatients.map((user, index) => (
            <div
              key={user._id}
              className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6"
            >
              {/* Basic Info Table */}
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-collapse border border-gray-300">
                  <tbody>
                    <tr className="bg-gray-100">
                      <td className="border px-4 py-2 font-semibold">Name</td>
                      <td className="border px-4 py-2">
                        <Link
                          to={`/dashboard/patient/${user._id}`}
                          className="text-blue-600 hover:underline font-semibold"
                        >
                          {user.userDetails?.name || "N/A"}
                        </Link>
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Email</td>
                      <td className="border px-4 py-2">
                        {user.email || "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="border px-4 py-2 font-semibold">
                        Account Status
                      </td>
                      <td className="border px-4 py-2">
                        <span
                          className={`px-2 py-1 rounded-md font-semibold ${user.status == "active"
                            ? "text-green-600 "
                            : "text-red-600"
                            }`}
                        >
                          {user.status == "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Created At
                      </td>
                      <td className="border px-4 py-2">
                        {formatDate(user.createdAt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Toggle Details Button */}
              <div className="text-center mb-4">
                <button
                  onClick={() => toggleTile(user._id)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {expandedTiles[user._id] ? "View Less" : "View More"}
                </button>
              </div>

              {/* Expanded Content */}
              {expandedTiles[user._id] && (
                <div className="border-t pt-4 space-y-6">
                  {/* User Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                      User Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <tbody>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Gender
                            </td>
                            <td className="border px-4 py-2">
                              {user.userDetails?.gender || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Date of Birth
                            </td>
                            <td className="border px-4 py-2">
                              {user.userDetails?.dateOfBirth
                                ? formatDate(user.userDetails.dateOfBirth)
                                : "N/A"}
                            </td>
                          </tr>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Contact
                            </td>
                            <td className="border px-4 py-2">
                              {user.userDetails?.contact?.mobile || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Address
                            </td>
                            <td className="border px-4 py-2">
                              {user.userDetails?.contact?.address || "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Medical Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                      Medical Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <tbody>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Blood Group
                            </td>
                            <td className="border px-4 py-2">
                              {user.medicalDetails?.bloodGroup || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Medical History
                            </td>
                            <td className="border px-4 py-2">
                              {Array.isArray(
                                user.medicalDetails?.medicalHistory
                              )
                                ? user.medicalDetails.medicalHistory.join(", ")
                                : user.medicalDetails?.medicalHistory || "N/A"}
                            </td>
                          </tr>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Past Dental History
                            </td>
                            <td className="border px-4 py-2">
                              {renderDentalHistory(
                                user.medicalDetails?.pastDentalHistory
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Previous OPD */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                      Previous OPD Records
                    </h3>
                    {user?.opdForms && user.opdForms.length > 0 ? (
                      <ul className="list-disc list-inside ml-4 text-blue-600">
                        {user.opdForms.map((opd) => (
                          <li key={opd._id} className="mb-1">
                            {opd.opdNumber} ({opd.checkupInfo} -{" "}
                            {formatDate(opd.createdAt)})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ml-4 text-gray-600">
                        No OPD records found.
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
                    <button
                      className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 focus:outline-none font-bold"
                      onClick={() => openModal(user)}
                    >
                      Create OPD
                    </button>
                    <button
                      className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 focus:outline-none font-bold"
                      onClick={() => openAppointmentModal(user)}
                    >
                      Create Appointment
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : !loading ? (
          <div className="text-center text-gray-600 py-8">
            No patients found matching your search criteria.
          </div>
        ) : null}
      </div>

      {/* Users List For Desktop */}
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6 hidden md:block">
        {!loading && allPatients?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 rounded-lg shadow-md">
              <thead>
                <tr className="bg-blue-600 text-white text-center border border-gray-300">
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Name
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Email
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Account Status
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Created At
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    More Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {allPatients.map((user) => (
                  <React.Fragment key={user._id}>
                    <tr className="bg-white text-gray-800 border-b hover:bg-gray-100 transition-all text-center">
                      <td className="px-4 py-3 font-semibold text-blue-600 border border-gray-300">
                        <Link
                          to={`/dashboard/patient/${user._id}`}
                          className="hover:underline"
                        >
                          {user.userDetails?.name || "N/A"}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700 border border-gray-300">
                        {user.email || "N/A"}
                      </td>
                      <td className="px-4 py-3 border border-gray-300">
                        <span
                          className={`px-3 py-1 rounded-lg font-semibold inline-block ${user.status == "active"
                            ? "text-green-600 "
                            : "text-red-600"
                            }`}
                        >
                          {user.status == "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 border border-gray-300">
                        {formatDate(user.createdAt)}
                      </td>

                      <td className="px-4 py-3 text-gray-700 border border-gray-300">
                        <button
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 shadow-md hover:shadow-lg transition-all font-bold"
                          onClick={() => toggleTile(user._id)}
                        >
                          {expandedTiles[user._id] ? "View Less" : "View More"}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {expandedTiles[user._id] && (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-4 pb-4 bg-gray-100 border border-gray-300"
                        >
                          <div className="p-6">
                            <h2 className="text-3xl font-bold text-blue-700 mb-6 flex items-center gap-2">
                              <User size={28} className="text-blue-600" />
                              Patient Details
                            </h2>

                            {/* Responsive Grid Layout */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                              {/* Personal Info Card */}
                              <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                                {/* Header with gradient background */}
                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5 text-white">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                      <User size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold">
                                      Personal Information
                                    </h3>
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 h-60 overflow-y-auto">
                                  <div className="space-y-4">
                                    {/* Gender */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <User
                                          size={16}
                                          className="text-blue-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Gender
                                        </p>
                                        <p className="text-gray-800 font-semibold mt-1 break-words">
                                          {user.userDetails?.gender ||
                                            "Not specified"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <Calendar
                                          size={16}
                                          className="text-blue-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Date of Birth
                                        </p>
                                        <p className="text-gray-800 font-semibold mt-1 break-words">
                                          {user.userDetails?.dateOfBirth
                                            ? formatDate(
                                              user.userDetails.dateOfBirth
                                            )
                                            : "Not provided"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Contact */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <Phone
                                          size={16}
                                          className="text-blue-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Contact
                                        </p>
                                        <p className="text-gray-800 font-semibold mt-1 break-all font-mono text-lg">
                                          {user.userDetails?.contact?.mobile ||
                                            "Not provided"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Address */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <MapPin
                                          size={16}
                                          className="text-blue-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Address
                                        </p>
                                        <p className="text-gray-800 font-medium mt-1 break-words leading-relaxed">
                                          {user.userDetails?.contact?.address ||
                                            "Not provided"}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Medical Details Card */}
                              <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 border border-green-200/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                                {/* Header with gradient background */}
                                <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-5 text-white">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                      <Heart size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold">
                                      Medical Details
                                    </h3>
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 h-60 overflow-y-auto">
                                  <div className="space-y-4">
                                    {/* Blood Group */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-red-100 rounded-lg">
                                        <Droplet
                                          size={16}
                                          className="text-red-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Blood Group
                                        </p>
                                        <p className="text-gray-800 font-bold mt-1 text-xl ">
                                          {user.medicalDetails?.bloodGroup ||
                                            "Unknown"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Medical History */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-green-100 rounded-lg">
                                        <FileText
                                          size={16}
                                          className="text-green-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Medical History
                                        </p>
                                        <div className="mt-2">
                                          {(() => {
                                            const history = Array.isArray(
                                              user.medicalDetails
                                                ?.medicalHistory
                                            )
                                              ? user.medicalDetails
                                                .medicalHistory
                                              : user.medicalDetails?.medicalHistory?.split(
                                                ","
                                              ) || [];

                                            if (
                                              history.length === 0 ||
                                              (history.length === 1 &&
                                                !history[0])
                                            ) {
                                              return (
                                                <p className="text-gray-500 italic">
                                                  No medical history recorded
                                                </p>
                                              );
                                            }

                                            return (
                                              <div className="flex flex-wrap gap-2">
                                                {history.map((item, index) => (
                                                  <span
                                                    key={index}
                                                    className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-medium"
                                                  >
                                                    {item.trim()}
                                                  </span>
                                                ))}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Past Dental History */}
                                    <div className="flex items-start gap-4 p-3 bg-white/60 rounded-xl backdrop-blur-sm transition-all hover:bg-white/80">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <FaTooth
                                          size={16}
                                          className="text-blue-600"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                          Past Dental History
                                        </p>
                                        <div className="mt-1 text-gray-800 font-medium">
                                          {renderDentalHistory(
                                            user.medicalDetails
                                              ?.pastDentalHistory
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Previous OPD Card */}
                              <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-200/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                                {/* Header with gradient background */}
                                <div className="bg-gradient-to-r from-purple-600 to-violet-700 p-5 text-white">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                      <ClipboardList
                                        size={24}
                                        className="text-white"
                                      />
                                    </div>
                                    <h3 className="text-xl font-bold">
                                      Previous OPD
                                    </h3>
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 h-60 overflow-y-auto">
                                  {user?.opdForms &&
                                    user.opdForms.length > 0 ? (
                                    <div className="space-y-3">
                                      {user.opdForms.map((opd, index) => (
                                        <div
                                          key={opd._id}
                                          className="group/item relative p-4 bg-white/60 rounded-xl backdrop-blur-sm border border-purple-200/30 transition-all hover:bg-white/80 hover:shadow-lg hover:scale-[1.02]"
                                        >
                                          {/* OPD Number Badge */}
                                          <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                                            #{index + 1}
                                          </div>

                                          <div className="flex items-start gap-3">
                                            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                                              <ClipboardList
                                                size={16}
                                                className="text-purple-600"
                                              />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h4 className="font-bold text-purple-800 text-lg group-hover/item:text-purple-900 transition-colors">
                                                {opd.opdNumber}
                                              </h4>
                                              <p className="text-gray-600 font-medium mt-1 break-words">
                                                {opd.checkupInfo}
                                              </p>
                                              <div className="flex items-center gap-2 mt-2">
                                                <Calendar
                                                  size={14}
                                                  className="text-gray-400"
                                                />
                                                <span className="text-sm text-gray-500 font-medium">
                                                  {formatDate(opd.createdAt)}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-center">
                                      <div className="p-4 bg-white/60 rounded-full mb-4">
                                        <ClipboardList
                                          size={48}
                                          className="text-gray-400"
                                        />
                                      </div>
                                      <p className="text-gray-500 font-medium text-lg">
                                        No OPD records found
                                      </p>
                                      <p className="text-gray-400 text-sm mt-1">
                                        Records will appear here once created
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center space-x-4 mt-8">
                              <button
                                className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 shadow-lg hover:shadow-xl transition-all font-bold"
                                onClick={() => openModal(user)}
                              >
                                Create OPD
                              </button>
                              <button
                                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all font-bold"
                                onClick={() => openAppointmentModal(user)}
                              >
                                Create Appointment
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : !loading ? (
          <div className="text-center text-gray-600 py-8">
            No patients found matching your search criteria.
          </div>
        ) : null}
      </div>

      {/* Enhanced Pagination Controls */}
      {totalPages > 1 && !loading && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            {/* Pagination Info */}
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
              {totalRecords} patients
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <FaChevronLeft size={12} />
                Previous
              </button>

              {/* First Page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 4 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {/* Page Numbers */}
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNum
                    ? "bg-blue-600 text-white border border-blue-600"
                    : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  {pageNum}
                </button>
              ))}

              {/* Last Page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <FaChevronRight size={12} />
              </button>
            </div>

            {/* Items Per Page Selector */}
            <div className="flex items-center gap-2">
              <label
                htmlFor="bottomItemsPerPage"
                className="text-sm font-medium text-gray-700"
              >
                Items per page:
              </label>
              <select
                id="bottomItemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Chief Complaint Modal */}
      {isModalOpen && selectedUser && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          aria-modal="true"
          role="dialog"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">
              Select Chief Complaint
            </h2>
            <div className="mb-4">
              <span className="font-semibold">Age:</span>{" "}
              {age !== "N/A" ? age : "Not Available"}
            </div>
            <div className="w-full">
              <label className="font-semibold block mb-2">
                Chief Complaint: <span style={{ color: "red" }}> * </span>
              </label>
              <select
                value={checkupInfo}
                onChange={(e) => setCheckupInfo(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="" disabled>
                  Select a reason...
                </option>
                {dentalReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-6">
              <button
                className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 focus:outline-none font-bold"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none font-bold"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {isAppointmentModalOpen && selectedUser && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          aria-modal="true"
          role="dialog"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">
              Create an Appointment
            </h2>
            <form onSubmit={handleAppointmentSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="appointmentDate"
                  className="block font-semibold mb-1"
                >
                  Preferred Appointment Date:{" "}
                  <span style={{ color: "red" }}> * </span>
                </label>
                <input
                  type="date"
                  id="appointmentDate"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={today.toISOString().split("T")[0]}
                  max={maxDate.toISOString().split("T")[0]}
                  required
                />
              </div>
              <div>
                <label htmlFor="timeBlock" className="block font-semibold mb-1">
                  Preferred Time Block:{" "}
                  <span style={{ color: "red" }}> * </span>
                </label>
                <select
                  id="timeBlock"
                  value={timeBlock}
                  onChange={(e) => setTimeBlock(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>
                    Select a time block
                  </option>
                  {timeBlocks.map((block) => (
                    <option key={block} value={block}>
                      {block}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="reason" className="block font-semibold mb-1">
                  Reason for Visit: <span style={{ color: "red" }}> * </span>
                </label>

                <select
                  id="reason"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (e.target.value !== "Other") setOtherReason("");
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  {reasonOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                {reason === "Others" && (
                  <input
                    type="text"
                    placeholder="Please specify"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                    required={reason === "Other"} // ✅ only required when shown
                  />
                )}
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 focus:outline-none font-bold"
                  onClick={closeAppointmentModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none font-bold"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer />
    </AnimatedContainer>
  );
};

export default UsersList;
